/**
 * DecisionFlow — Login Screen
 * S279 redesign — OI-LOGIN-REDESIGN
 *
 * Layout (per locked spec):
 *   Left  60% — Marketing panel:
 *     ├─ Badge + headline + framing line
 *     ├─ Persona tabs (Finance | Sales Ops | Compensation)
 *     ├─ Single dynamic persona insight card
 *     ├─ CTA row (Book a Demo + View Project Brief)
 *     └─ Industry metrics strip (bottom, low emphasis)
 *   Right 40% — Login form (application chrome):
 *     ├─ Top-right: theme toggle
 *     ├─ Product identity (data-fit LLC / DecisionFlow)
 *     ├─ Form header
 *     ├─ Enterprise SSO stubs (above form — trust signal)
 *     ├─ Provisioning clarification (below SSO, above divider)
 *     ├─ "or continue with email" divider
 *     ├─ Email + password fields
 *     ├─ Forgot password
 *     └─ Sign In button
 *
 * Theme:   data-theme attribute on <html> — light/dark/contrast.
 *          ThemeButton component used. localStorage persists.
 *          Login page defaults to light for first-time visitors.
 * Brand:   Left panel uses hardcoded brand navy #0A2463 — deliberately
 *          outside the app token system. Right panel uses full token system.
 * Font:    IBM Plex Sans throughout. Loaded via index.css Google Fonts import.
 * Cards:   Card system retained in registry for future use but not rendered
 *          in the new layout. Persona content is self-contained in this file.
 *
 * S665 OI-LOGIN-METHOD-ROUTING:
 *   On email field blur, calls GET /api/v1/auth/login-method?email=&tenant_slug=
 *   Routes to correct auth flow based on response:
 *     local      → show password field (existing behaviour)
 *     magic_link → hide password field, show "Send login link" button
 *     oidc       → hide password field, activate SSO buttons
 *   404 or error → silent fail, default to password form (do not reveal
 *   user existence). Raw fetch() used — consistent with other auth screens.
 */

import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.jsx";
import ThemeButton from "../components/shared/ThemeButton.jsx";

// ── Shell redirect map ──────────────────────────────────────────────────────
const SHELL_ROOT = {
  comp_admin:         "/admin/",
  finance:            "/finance/",
  manager:            "/manager/",
  rep:                "/rep/",
  tenant_super_admin: "/tenant-admin/",
  tool_admin:         "/tenant-admin/",
  platform_admin:     "/tenant-admin/",
};

// ── Error code map ──────────────────────────────────────────────────────────
function errorFromCode(code, message) {
  switch (code) {
    case "AUTH_TOKEN_INVALID":
      return { kind: "inline", text: "Email or password is incorrect." };
    case "AUTH_ACCOUNT_LOCKED":
      return { kind: "banner", text: "Account locked after 5 consecutive failures. Contact support at support@data-fit.com." };
    case "TENANT_EXPIRED":
      return { kind: "banner", text: "Your tenant trial has expired. Contact data-fit LLC to continue." };
    case "TENANT_SUSPENDED":
      return { kind: "banner", text: "This account is suspended. Contact support at support@data-fit.com." };
    default:
      return { kind: "banner", text: message || "Service unavailable. Try again in a moment." };
  }
}

// ── Cookie notice helpers ───────────────────────────────────────────────────
function getCookieDismissed() {
  try { return localStorage.getItem("df-cookie-dismissed") === "1"; } catch (_) { return false; }
}
function setCookieDismissed() {
  try { localStorage.setItem("df-cookie-dismissed", "1"); } catch (_) {}
}

// ── Persona content — locked from SME consultation ─────────────────────────
const PERSONAS = [
  {
    id: "finance",
    label: "Finance",
    headline: "Payout exposure should not be discovered at close.",
    supporting: "DecisionFlow surfaces P50, P75, and P90 compensation exposure before payroll and before the books close.",
    response: "Finance sees quota, accelerators, exceptions, and payout exposure in one governed operating model.",
    quote: "I approve a comp budget at plan case. I discover the real number at close.",
  },
  {
    id: "sales-ops",
    label: "Sales Ops",
    headline: "Quota decisions create financial exposure.",
    supporting: "Over-assignment, accelerators, and exception handling all change payout cost before Finance sees the impact.",
    response: "DecisionFlow connects quota deployment, operational exceptions, and payout exposure before plans go live.",
    quote: "I set quota without seeing what it costs at P90.",
  },
  {
    id: "compensation",
    label: "Compensation",
    headline: "Every payout dispute becomes operational debt.",
    supporting: "Disputes, adjustments, and payout explanations are governed through structured workflows with full audit visibility.",
    response: "AI explains payout calculations instantly while approvals and disputes remain fully traceable.",
    quote: "Every dispute becomes an email thread.",
  },
];

// ── Industry metrics — low emphasis, bottom of left panel ──────────────────
const METRICS = [
  { stat: "81%", text: "of companies rate their comp plan as less than highly effective", source: "Alexander Group, 2024" },
  { stat: "54%", text: "operate without dedicated Sales Performance Management technology", source: "WorldatWork + OpenSymmetry, 2024" },
  { stat: "82%", text: "of companies use accelerators — creating near-universal payout exposure risk", source: "ICONIQ Growth, 2024" },
];

// ── Component ───────────────────────────────────────────────────────────────
export default function Login() {
  const [activePersona, setActivePersona]         = useState(0);
  const [cardVisible, setCardVisible]             = useState(true);
  const [email, setEmail]                         = useState("");
  const [password, setPassword]                   = useState("");
  const [submitting, setSubmitting]               = useState(false);
  const [errorMessage, setErrorMessage]           = useState(null);
  const [errorKind, setErrorKind]                 = useState(null);
  const [cookieDismissed, setCookieDismissedState] = useState(getCookieDismissed);
  const [ssoLoading, setSsoLoading]               = useState(false);
  const [showPassword, setShowPassword]           = useState(false);

  // S665 OI-LOGIN-METHOD-ROUTING — auth method state
  const [authMethod, setAuthMethod]               = useState(null); // null | "local" | "magic_link" | "oidc"
  const [authMethodLoading, setAuthMethodLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent]         = useState(false);
  const [magicLinkError, setMagicLinkError]       = useState(null);

  const { login } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();

  // SSO: read ?tenant= from URL — IT provisions per-tenant login URLs.
  // e.g. /login?tenant=meridian-global activates SSO buttons for that org.
  const tenantSlug     = new URLSearchParams(location.search).get("tenant") ?? null;
  // SSO error passed back from SsoCallback.jsx via location.state.
  const locationSsoError = location.state?.ssoError ?? null;
  // Success message passed back from ResetPassword / SetPassword on completion.
  const locationSuccess  = location.state?.successMessage ?? null;

  // Initialise theme — default light for first-time visitors
  useEffect(() => {
    const saved = (() => { try { return localStorage.getItem("decisionflow-theme"); } catch (_) { return null; } })();
    const theme = saved || "light";
    document.documentElement.setAttribute("data-theme", theme);
  }, []);

  // Demo launcher handoff — ?session=ready param signals that the launcher
  // has already authenticated and set the session cookie. We probe /auth/me
  // and redirect to the correct shell if the session is valid.
  // Zero effect on normal login flow — param is never present in production.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("session") !== "ready") return;
    (async () => {
      try {
        const res = await fetch("/api/v1/auth/me", { credentials: "include" });
        if (res.ok) {
          const user = await res.json();
          const shellRoot = SHELL_ROOT[user.user_type] ?? "/";
          navigate(shellRoot, { replace: true });
        }
      } catch (_) {
        // Session probe failed — fall through to normal login form.
      }
    })();
  }, []);

  function dismissCookie() {
    setCookieDismissed();
    setCookieDismissedState(true);
  }

  function switchPersona(idx) {
    if (idx === activePersona) return;
    setCardVisible(false);
    setTimeout(() => {
      setActivePersona(idx);
      setCardVisible(true);
    }, 160);
  }

  // S665 OI-LOGIN-METHOD-ROUTING — look up auth method on email blur.
  // Requires tenantSlug — without it the endpoint cannot resolve the tenant.
  // Silent fail on 404 or error: default to password form, do not reveal
  // whether the email exists in the system.
  async function handleEmailBlur() {
    if (!email || email.length < 3 || !tenantSlug) return;
    setAuthMethodLoading(true);
    setAuthMethod(null);
    setMagicLinkSent(false);
    setMagicLinkError(null);
    try {
      const res = await fetch(
        `/api/v1/auth/login-method?email=${encodeURIComponent(email)}&tenant_slug=${encodeURIComponent(tenantSlug)}`,
        { method: "GET", headers: { "Content-Type": "application/json" } }
      );
      if (res.ok) {
        const data = await res.json();
        setAuthMethod(data.auth_method ?? null);
      }
      // 404 (user not found) or any other error — leave authMethod null.
      // Password form is the safe default. Do not surface user existence.
    } catch (_) {
      // Network error — silent fail, default to password form.
    } finally {
      setAuthMethodLoading(false);
    }
  }

  // S665 OI-LOGIN-METHOD-ROUTING — send magic link for magic_link users.
  // Always-200 endpoint — show sent confirmation regardless of outcome.
  async function handleMagicLinkRequest() {
    if (submitting || magicLinkSent) return;
    setSubmitting(true);
    setMagicLinkError(null);
    try {
      await fetch("/api/v1/auth/magic-link/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, tenant_slug: tenantSlug }),
      });
      // Always show sent state — endpoint always returns 200.
      setMagicLinkSent(true);
    } catch (_) {
      setMagicLinkError("Could not send login link. Check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  }

  function handleSsoClick(provider) {
    if (!tenantSlug || ssoLoading) return;
    setSsoLoading(true);
    // Redirect to backend SSO initiate — backend returns 302 to IdP.
    // provider param selects which IdP config row to use (microsoft | google).
    window.location.href = `/api/v1/auth/sso/initiate?tenant_slug=${encodeURIComponent(tenantSlug)}&provider=${provider}`;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setErrorMessage(null);
    setErrorKind(null);

    const result = await login(email, password);

    if (result.ok) {
      const shellRoot   = SHELL_ROOT[result.user?.user_type] ?? "/login";
      const destination = location.state?.from || shellRoot;
      navigate(destination, { replace: true });
    } else {
      const mapped = errorFromCode(result.error?.code, result.error?.message);
      setErrorMessage(mapped.text);
      setErrorKind(mapped.kind);
      setSubmitting(false);
    }
  }

  const persona = PERSONAS[activePersona];

  // S665 — derived display flags for auth method routing.
  // showPasswordForm: true when authMethod is null (default/loading) or local.
  // showMagicLink: true when authMethod is magic_link.
  // ssoActive: true when authMethod is oidc (overrides tenantSlug-only gate).
  const showPasswordForm = authMethod === null || authMethod === "local";
  const showMagicLink    = authMethod === "magic_link";
  const ssoActive        = !!tenantSlug || authMethod === "oidc";

  // ── Scoped CSS ─────────────────────────────────────────────────────────────
  const css = `
    /* ── Root layout ── */
    .df-login-root {
      display: flex;
      height: 100vh;
      font-family: "IBM Plex Sans", "Segoe UI", Arial, sans-serif;
      overflow: hidden;
      min-width: 1280px;
    }

    /* ══════════════════════════════════════════════════════
       LEFT PANEL — 60% — Brand navy, marketing narrative
       Hardcoded brand colours — deliberately outside token system
    ══════════════════════════════════════════════════════ */
    .df-left {
      width: 60%;
      display: flex;
      flex-direction: column;
      height: 100vh;
      overflow: hidden;
      background: #0A2463;
      position: relative;
      padding: 48px 56px;
    }

    /* Subtle radial glow — depth without noise */
    .df-left::before {
      content: '';
      position: absolute;
      top: -120px; left: -80px;
      width: 600px; height: 600px;
      background: radial-gradient(ellipse at center, rgba(27,79,216,0.18) 0%, transparent 70%);
      pointer-events: none;
    }

    /* ── Badge ── */
    .df-badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: rgba(184,150,62,0.15);
      border: 1px solid rgba(184,150,62,0.3);
      color: #D4AF5A;
      font-size: var(--font-sz-xs);
      font-weight: 600;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      padding: 5px 14px;
      border-radius: 20px;
      margin-bottom: 28px;
      width: fit-content;
    }
    .df-badge-dot {
      width: 6px; height: 6px;
      background: #B8963E;
      border-radius: 50%;
      animation: df-pulse 2s infinite;
    }
    @keyframes df-pulse {
      0%, 100% { opacity: 1; }
      50%       { opacity: 0.35; }
    }

    /* ── Headline ── */
    .df-headline {
      font-size: var(--font-sz-2xl);
      font-weight: 700;
      color: #ffffff;
      line-height: 1.2;
      letter-spacing: -0.02em;
      margin-bottom: 16px;
      max-width: 560px;
    }

    /* ── Framing line ── */
    .df-framing {
      font-size: var(--font-sz-base);
      font-weight: 400;
      color: rgba(255,255,255,0.65);
      line-height: 1.7;
      max-width: 540px;
      margin-bottom: 36px;
    }

    /* ── Persona tabs ── */
    .df-tabs {
      display: flex;
      gap: 0;
      border-bottom: 1px solid rgba(255,255,255,0.12);
      margin-bottom: 28px;
    }
    .df-tab {
      padding: 8px 20px 10px;
      font-size: var(--font-sz-sm);
      font-weight: 500;
      color: rgba(255,255,255,0.45);
      background: transparent;
      border: none;
      border-bottom: 2px solid transparent;
      margin-bottom: -1px;
      cursor: pointer;
      transition: color 150ms, border-color 150ms;
      font-family: inherit;
      letter-spacing: 0.01em;
    }
    .df-tab:hover { color: rgba(255,255,255,0.75); }
    .df-tab.active {
      color: #D4AF5A;
      border-bottom-color: #B8963E;
      font-weight: 600;
    }

    /* ── Persona card ── */
    .df-persona-card {
      background: rgba(255,255,255,0.05);
      border: 1px solid rgba(255,255,255,0.1);
      border-radius: 10px;
      padding: 28px 32px;
      margin-bottom: 32px;
      flex-shrink: 0;
      transition: opacity 160ms ease, transform 160ms ease;
    }
    .df-persona-card.hidden {
      opacity: 0;
      transform: translateY(6px);
    }
    .df-persona-card.visible {
      opacity: 1;
      transform: translateY(0);
    }
    .df-persona-headline {
      font-size: var(--font-sz-lg);
      font-weight: 700;
      color: #ffffff;
      line-height: 1.3;
      margin-bottom: 12px;
    }
    .df-persona-supporting {
      font-size: var(--font-sz-sm);
      color: rgba(255,255,255,0.62);
      line-height: 1.65;
      margin-bottom: 14px;
    }
    .df-persona-response {
      font-size: var(--font-sz-sm);
      color: rgba(255,255,255,0.85);
      line-height: 1.6;
      padding: 12px 16px;
      background: rgba(27,79,216,0.2);
      border-left: 3px solid #1B4FD8;
      border-radius: 4px;
      margin-bottom: 16px;
    }
    .df-persona-quote {
      font-size: var(--font-sz-sm);
      font-style: italic;
      color: rgba(255,255,255,0.38);
      line-height: 1.6;
      border-top: 1px solid rgba(255,255,255,0.08);
      padding-top: 14px;
    }
    .df-persona-quote::before { content: '"'; }
    .df-persona-quote::after  { content: '"'; }

    /* ── CTA row ── */
    .df-cta-row {
      display: flex;
      align-items: center;
      gap: 16px;
      margin-bottom: auto;
    }
    .df-cta-primary {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      background: #B8963E;
      color: #ffffff;
      font-size: var(--font-sz-sm);
      font-weight: 600;
      padding: 11px 24px;
      border-radius: 7px;
      text-decoration: none;
      border: none;
      cursor: pointer;
      font-family: inherit;
      transition: background 150ms;
      letter-spacing: 0.01em;
    }
    .df-cta-primary:hover { background: #D4AF5A; text-decoration: none; }
    .df-cta-secondary {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      color: rgba(255,255,255,0.55);
      font-size: var(--font-sz-sm);
      font-weight: 500;
      text-decoration: none;
      transition: color 150ms;
    }
    .df-cta-secondary:hover { color: rgba(255,255,255,0.9); text-decoration: none; }

    /* ── Metrics strip — bottom, low emphasis ── */
    .df-metrics {
      display: flex;
      gap: 24px;
      padding-top: 24px;
      border-top: 1px solid rgba(255,255,255,0.08);
      margin-top: 32px;
    }
    .df-metric {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    .df-metric-stat {
      font-size: var(--font-sz-lg);
      font-weight: 700;
      color: rgba(255,255,255,0.35);
      letter-spacing: -0.01em;
      line-height: 1;
    }
    .df-metric-text {
      font-size: var(--font-sz-xs);
      color: rgba(255,255,255,0.28);
      line-height: 1.5;
    }
    .df-metric-source {
      font-size: var(--font-sz-xs);
      color: rgba(255,255,255,0.18);
      font-style: italic;
    }

    /* ══════════════════════════════════════════════════════
       RIGHT PANEL — 40% — Application chrome, token system
    ══════════════════════════════════════════════════════ */
    .df-right {
      width: 40%;
      display: flex;
      flex-direction: column;
      height: 100vh;
      overflow: hidden;
      background: rgb(var(--color-bg));
      position: relative;
      border-left: 1px solid rgb(var(--color-border));
    }

    /* Theme toggle — top right */
    .df-right-top {
      display: flex;
      justify-content: flex-end;
      padding: 20px 28px 0;
      flex-shrink: 0;
    }

    /* Form area — vertically centered */
    .df-form-outer {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: 0 56px;
      overflow-y: auto;
    }

    /* Product identity */
    .df-product-identity {
      margin-bottom: 28px;
    }
    .df-identity-brand {
      font-size: var(--font-sz-xs);
      font-weight: 500;
      color: rgb(var(--color-text-muted));
      letter-spacing: 0.04em;
      margin-bottom: 3px;
    }
    .df-identity-name {
      font-size: var(--font-sz-xl);
      font-weight: 700;
      color: rgb(var(--color-primary));
      letter-spacing: -0.01em;
      line-height: 1.1;
    }

    /* Form header */
    .df-form-header {
      margin-bottom: 28px;
    }
    .df-form-title {
      font-size: var(--font-sz-lg);
      font-weight: 700;
      color: rgb(var(--color-text));
      margin-bottom: 6px;
    }
    .df-form-subtitle {
      font-size: var(--font-sz-sm);
      color: rgb(var(--color-text-muted));
    }

    /* Error banner */
    .df-error-banner {
      background: rgba(var(--color-danger), 0.08);
      border: 1px solid rgb(var(--color-danger));
      border-radius: 7px;
      color: rgb(var(--color-danger));
      font-size: var(--font-sz-sm);
      padding: 11px 14px;
      margin-bottom: 20px;
      line-height: 1.5;
    }

    /* Success banner */
    .df-success-banner {
      background: rgba(var(--color-success, 34,197,94), 0.08);
      border: 1px solid rgba(var(--color-success, 34,197,94), 0.3);
      border-radius: 7px;
      color: rgb(var(--color-success, 22,163,74));
      font-size: var(--font-sz-sm);
      padding: 11px 14px;
      margin-bottom: 20px;
      line-height: 1.5;
    }

    /* SSO section */
    .df-sso-section {
      margin-bottom: 20px;
    }
    .df-sso-label {
      font-size: var(--font-sz-xs);
      font-weight: 600;
      color: rgb(var(--color-text-muted));
      text-transform: uppercase;
      letter-spacing: 0.08em;
      margin-bottom: 10px;
    }
    .df-sso-btn {
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;
      padding: 10px 14px;
      border: 1px solid rgb(var(--color-border));
      border-radius: 7px;
      background: rgb(var(--color-surface));
      color: rgb(var(--color-text-muted));
      font-size: var(--font-sz-sm);
      font-weight: 500;
      font-family: inherit;
      cursor: not-allowed;
      margin-bottom: 8px;
      position: relative;
      justify-content: space-between;
    }
    .df-sso-btn:not([aria-disabled="true"]) {
      cursor: pointer;
    }
    .df-sso-btn:not([aria-disabled="true"]):hover {
      background: rgb(var(--color-pressed));
    }
    .df-sso-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    .df-sso-btn-left {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    .df-sso-soon {
      font-size: var(--font-sz-xs);
      font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: rgb(var(--color-text-muted));
      background: rgb(var(--color-surface-muted));
      border: 1px solid rgb(var(--color-border));
      padding: 2px 8px;
      border-radius: 10px;
    }
    .df-sso-icon {
      width: 18px;
      height: 18px;
      border-radius: 3px;
      background: rgb(var(--color-surface-muted));
      flex-shrink: 0;
    }

    /* Provisioning clarification — below SSO, above divider */
    .df-provisioning {
      font-size: var(--font-sz-xs);
      color: rgb(var(--color-text-muted));
      line-height: 1.5;
      margin-bottom: 20px;
      padding: 10px 12px;
      background: rgb(var(--color-surface-muted));
      border-radius: 6px;
      border-left: 2px solid rgb(var(--color-border-strong));
    }

    /* Divider */
    .df-divider {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 20px;
      color: rgb(var(--color-text-muted));
      font-size: var(--font-sz-xs);
    }
    .df-divider-line {
      flex: 1;
      height: 1px;
      background: rgb(var(--color-border));
    }

    /* Fields */
    .df-field {
      margin-bottom: 18px;
    }
    .df-label {
      display: block;
      font-size: var(--font-sz-sm);
      font-weight: 500;
      color: rgb(var(--color-text));
      margin-bottom: 6px;
    }
    .df-input {
      width: 100%;
      padding: 10px 13px;
      border: 1px solid rgb(var(--color-border));
      border-radius: 7px;
      background: rgb(var(--color-surface));
      color: rgb(var(--color-text));
      font-size: var(--font-sz-sm);
      font-family: inherit;
      transition: border-color 150ms, box-shadow 150ms;
      outline: none;
    }
    .df-input::placeholder {
      color: rgb(var(--color-placeholder));
      font-style: italic;
    }
    .df-input:focus {
      border-color: rgb(var(--color-focus-ring));
      box-shadow: 0 0 0 3px rgba(var(--color-focus-ring), 0.15);
    }
    .df-input:disabled { opacity: 0.5; cursor: not-allowed; }

    /* Password field — show/hide wrapper */
    .df-input-wrap {
      position: relative;
      display: flex;
      align-items: center;
    }
    .df-input--padded {
      padding-right: 52px;
    }
    .df-pw-reveal {
      position: absolute;
      right: 10px;
      background: none;
      border: none;
      cursor: pointer;
      color: rgb(var(--color-text-muted));
      font-size: var(--font-sz-xs);
      font-family: inherit;
      font-weight: 500;
      padding: 4px 6px;
      border-radius: 4px;
      line-height: 1;
      flex-shrink: 0;
    }
    .df-pw-reveal:hover {
      color: rgb(var(--color-text));
      background: rgb(var(--color-surface-muted));
    }

    /* Inline error */
    .df-inline-error {
      font-size: var(--font-sz-xs);
      color: rgb(var(--color-danger));
      margin-top: -10px;
      margin-bottom: 14px;
    }

    /* Forgot password */
    .df-forgot-row {
      display: flex;
      justify-content: flex-end;
      margin-top: -8px;
      margin-bottom: 20px;
    }
    .df-forgot-btn {
      background: none;
      border: none;
      color: rgb(var(--color-primary));
      font-size: var(--font-sz-xs);
      font-family: inherit;
      cursor: pointer;
      padding: 0;
    }
    .df-forgot-btn:hover { text-decoration: underline; }

    /* Submit */
    .df-submit {
      width: 100%;
      padding: 12px;
      background: rgb(var(--color-primary));
      color: #ffffff;
      border: none;
      border-radius: 7px;
      font-size: var(--font-sz-base);
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
      transition: background 150ms;
      letter-spacing: 0.01em;
    }
    .df-submit:hover:not(:disabled) { background: rgb(var(--color-primary-hover)); }
    .df-submit:disabled { opacity: 0.5; cursor: not-allowed; }

    /* Magic link button — same weight as submit, distinct colour */
    .df-magic-btn {
      width: 100%;
      padding: 12px;
      background: rgb(var(--color-surface-muted));
      color: rgb(var(--color-text));
      border: 1px solid rgb(var(--color-border-strong));
      border-radius: 7px;
      font-size: var(--font-sz-base);
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
      transition: background 150ms, border-color 150ms;
      letter-spacing: 0.01em;
      margin-bottom: 0;
    }
    .df-magic-btn:hover:not(:disabled) {
      background: rgb(var(--color-pressed));
      border-color: rgb(var(--color-primary));
    }
    .df-magic-btn:disabled { opacity: 0.5; cursor: not-allowed; }

    /* Magic link sent confirmation */
    .df-magic-sent {
      background: rgba(var(--color-success, 34,197,94), 0.08);
      border: 1px solid rgba(var(--color-success, 34,197,94), 0.3);
      border-radius: 7px;
      color: rgb(var(--color-success, 22,163,74));
      font-size: var(--font-sz-sm);
      padding: 11px 14px;
      margin-bottom: 0;
      line-height: 1.5;
    }

    /* Auth method hint — shown below email when method resolved */
    .df-auth-hint {
      font-size: var(--font-sz-xs);
      color: rgb(var(--color-text-muted));
      margin-top: -10px;
      margin-bottom: 14px;
      font-style: italic;
    }

    /* Trademark disclaimer */
    .df-tm-disclaimer {
      font-size: var(--font-sz-xs);
      color: rgb(var(--color-text-muted));
      line-height: 1.5;
      margin-top: 16px;
      opacity: 0.7;
    }

    /* Right panel footer */
    .df-right-footer {
      flex-shrink: 0;
      padding: 16px 56px;
      border-top: 1px solid rgb(var(--color-border));
    }
    .df-right-footer-copy {
      font-size: var(--font-sz-xs);
      color: rgb(var(--color-text-muted));
      line-height: 1.6;
    }
    .df-right-footer-links {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-top: 6px;
      flex-wrap: wrap;
    }
    .df-footer-link {
      font-size: var(--font-sz-xs);
      color: rgb(var(--color-text-muted));
      text-decoration: none;
    }
    .df-footer-link:hover { text-decoration: underline; }
    .df-footer-sep { color: rgb(var(--color-border-strong)); }

    /* ── Cookie bar ── */
    .df-cookie-bar {
      position: fixed;
      bottom: 0; left: 0; right: 0;
      z-index: 100;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      padding: 12px 32px;
      background: rgb(var(--color-surface));
      border-top: 1px solid rgb(var(--color-border));
    }
    .df-cookie-text {
      font-size: var(--font-sz-xs);
      color: rgb(var(--color-text-muted));
      line-height: 1.5;
    }
    .df-cookie-link {
      color: rgb(var(--color-primary));
      text-decoration: none;
    }
    .df-cookie-link:hover { text-decoration: underline; }
    .df-cookie-dismiss {
      padding: 6px 16px;
      border-radius: 6px;
      font-family: inherit;
      font-size: var(--font-sz-xs);
      font-weight: 500;
      cursor: pointer;
      background: rgb(var(--color-surface-muted));
      border: 1px solid rgb(var(--color-border));
      color: rgb(var(--color-text));
      white-space: nowrap;
      flex-shrink: 0;
    }
    .df-cookie-dismiss:hover { background: rgb(var(--color-pressed)); }
  `;

  return (
    <>
      <style>{css}</style>

      <div className="df-login-root">

        {/* ══ LEFT: Marketing panel — brand navy ══ */}
        <div className="df-left">

          {/* Badge */}
          <div className="df-badge">
            <span className="df-badge-dot" aria-hidden="true" />
            Enterprise Compensation Operations
          </div>

          {/* Headline */}
          <div className="df-headline">
            Compensation operations, separated by role.
          </div>

          {/* Financial framing line */}
          <div className="df-framing">
            DecisionFlow connects compensation planning, payout exposure, approvals,
            and execution in one governed operating model.
          </div>

          {/* Persona tabs */}
          <div className="df-tabs" role="tablist" aria-label="View by role">
            {PERSONAS.map((p, idx) => (
              <button
                key={p.id}
                role="tab"
                aria-selected={idx === activePersona}
                className={`df-tab${idx === activePersona ? " active" : ""}`}
                onClick={() => switchPersona(idx)}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Dynamic persona card */}
          <div
            className={`df-persona-card${cardVisible ? " visible" : " hidden"}`}
            role="tabpanel"
            aria-label={`${persona.label} perspective`}
          >
            <div className="df-persona-headline">{persona.headline}</div>
            <div className="df-persona-supporting">{persona.supporting}</div>
            <div className="df-persona-response">{persona.response}</div>
            <div className="df-persona-quote">{persona.quote}</div>
          </div>

          {/* CTA row */}
          <div className="df-cta-row">
            <a
              href="https://decisionflow.data-fit.com/#contact"
              className="df-cta-primary"
              onClick={(e) => {
                e.preventDefault();
                // Opens the demo request modal on decisionflow.data-fit.com in new tab
                window.open("https://decisionflow.data-fit.com/#contact", "_blank");
              }}
            >
              Book a Demo
            </a>
            <a
              href="https://decisionflow.data-fit.com"
              className="df-cta-secondary"
              target="_blank"
              rel="noopener noreferrer"
            >
              Back to DecisionFlow →
            </a>
          </div>

          {/* Industry metrics strip — bottom, low emphasis */}
          <div className="df-metrics" aria-label="Industry context">
            {METRICS.map((m) => (
              <div key={m.stat} className="df-metric">
                <div className="df-metric-stat">{m.stat}</div>
                <div className="df-metric-text">{m.text}</div>
                <div className="df-metric-source">{m.source}</div>
              </div>
            ))}
          </div>

        </div>

        {/* ══ RIGHT: Login form — application chrome ══ */}
        <div className="df-right">

          {/* Theme toggle — top right, cycles light / dark / contrast */}
          <div className="df-right-top">
            <ThemeButton />
          </div>

          {/* Vertically centered form area */}
          <div className="df-form-outer">

            {/* Product identity */}
            <div className="df-product-identity">
              <div className="df-identity-brand">data-fit LLC</div>
              <div className="df-identity-name">DecisionFlow</div>
            </div>

            {/* Form header */}
            <div className="df-form-header">
              <div className="df-form-title">Sign in to DecisionFlow</div>
              <div className="df-form-subtitle">Access your compensation operations workspace.</div>
            </div>

            {/* Error banner — password login errors */}
            {errorKind === "banner" && errorMessage && (
              <div className="df-error-banner" role="alert">{errorMessage}</div>
            )}

            {/* Success banner — passed back from ResetPassword / SetPassword */}
            {locationSuccess && (
              <div className="df-success-banner" role="status">{locationSuccess}</div>
            )}

            {/* SSO error banner — errors returned from SsoCallback */}
            {locationSsoError && (
              <div className="df-error-banner" role="alert">{locationSsoError}</div>
            )}

            {/* Enterprise SSO — above form, trust signal.
                Active when tenantSlug present in URL OR login-method returns oidc. */}
            <div className="df-sso-section">
              <div className="df-sso-label">Enterprise SSO</div>
              {ssoActive ? (
                <>
                  <button
                    type="button"
                    className="df-sso-btn"
                    onClick={() => handleSsoClick("microsoft")}
                    disabled={ssoLoading || submitting}
                    aria-label="Continue with Microsoft SSO"
                  >
                    <div className="df-sso-btn-left">
                      <div className="df-sso-icon" style={{ background: "#00a1f1" }} aria-hidden="true" />
                      Continue with Microsoft
                    </div>
                    {ssoLoading && <span className="df-sso-soon">Redirecting…</span>}
                  </button>
                  <button
                    type="button"
                    className="df-sso-btn"
                    onClick={() => handleSsoClick("google")}
                    disabled={ssoLoading || submitting}
                    aria-label="Continue with Google SSO"
                  >
                    <div className="df-sso-btn-left">
                      <div className="df-sso-icon" style={{ background: "#4285f4" }} aria-hidden="true" />
                      Continue with Google
                    </div>
                    {ssoLoading && <span className="df-sso-soon">Redirecting…</span>}
                  </button>
                </>
              ) : (
                <>
                  <div className="df-sso-btn" aria-disabled="true">
                    <div className="df-sso-btn-left">
                      <div className="df-sso-icon" style={{ background: "#00a1f1" }} aria-hidden="true" />
                      Continue with Microsoft
                    </div>
                    <span className="df-sso-soon">Use your organisation login URL</span>
                  </div>
                  <div className="df-sso-btn" aria-disabled="true">
                    <div className="df-sso-btn-left">
                      <div className="df-sso-icon" style={{ background: "#4285f4" }} aria-hidden="true" />
                      Continue with Google
                    </div>
                    <span className="df-sso-soon">Use your organisation login URL</span>
                  </div>
                </>
              )}
            </div>

            {/* Provisioning clarification — below SSO, above divider */}
            <div className="df-provisioning">
              DecisionFlow environments are provisioned and operated by data-fit LLC.
            </div>

            {/* Divider */}
            <div className="df-divider" aria-hidden="true">
              <div className="df-divider-line" />
              <span>or continue with email</span>
              <div className="df-divider-line" />
            </div>

            {/* Email + auth-method-routed form */}
            <form onSubmit={handleSubmit} noValidate>
              <div className="df-field">
                <label className="df-label" htmlFor="df-email">Email address</label>
                <input
                  id="df-email"
                  className="df-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={handleEmailBlur}
                  autoComplete="username"
                  autoFocus
                  required
                  disabled={submitting}
                  placeholder="you@company.com"
                />
                {/* Auth method hint — shown after lookup resolves */}
                {authMethod === "magic_link" && !magicLinkSent && (
                  <div className="df-auth-hint">
                    This account uses passwordless login. We'll send you a secure link.
                  </div>
                )}
                {authMethod === "oidc" && (
                  <div className="df-auth-hint">
                    This account uses Single Sign-On. Use the SSO buttons above.
                  </div>
                )}
              </div>

              {/* Password field — shown for local auth or before method resolves */}
              {showPasswordForm && (
                <div className="df-field">
                  <label className="df-label" htmlFor="df-password">Password</label>
                  <div className="df-input-wrap">
                    <input
                      id="df-password"
                      className="df-input df-input--padded"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                      disabled={submitting}
                      placeholder="••••••••••"
                    />
                    <button
                      type="button"
                      className="df-pw-reveal"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      tabIndex={-1}
                    >
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>
              )}

              {errorKind === "inline" && errorMessage && (
                <div className="df-inline-error" role="alert">{errorMessage}</div>
              )}

              {/* Forgot password — only shown with password form */}
              {showPasswordForm && (
                <div className="df-forgot-row">
                  <button
                    type="button"
                    className="df-forgot-btn"
                    onClick={() => navigate("/forgot-password")}
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              {/* Magic link flow — send link button or sent confirmation */}
              {showMagicLink && (
                magicLinkSent ? (
                  <div className="df-magic-sent" role="status">
                    Login link sent. Check your email — the link expires in 15 minutes.
                  </div>
                ) : (
                  <>
                    {magicLinkError && (
                      <div className="df-error-banner" role="alert">{magicLinkError}</div>
                    )}
                    <button
                      type="button"
                      className="df-magic-btn"
                      onClick={handleMagicLinkRequest}
                      disabled={submitting || !email}
                    >
                      {submitting ? "Sending…" : "Send login link →"}
                    </button>
                  </>
                )
              )}

              {/* Sign in button — only shown for local auth or before method resolves */}
              {showPasswordForm && (
                <button
                  className="df-submit"
                  type="submit"
                  disabled={submitting || authMethodLoading || !email || !password}
                >
                  {submitting ? "Signing in…" : "Sign in →"}
                </button>
              )}
            </form>

            {/* Trademark disclaimer */}
            <div className="df-tm-disclaimer">
              Microsoft® and Google® are trademarks of their respective owners.
              data-fit LLC is not affiliated with or endorsed by Microsoft Corporation or Google LLC.
            </div>

          </div>

          {/* Right panel footer */}
          <div className="df-right-footer">
            <div className="df-right-footer-copy">
              DecisionFlow™ and data-fit™ are trademarks of data-fit LLC.
              © {new Date().getFullYear()} data-fit LLC. All rights reserved.
            </div>
            <div className="df-right-footer-links">
              <a href="https://www.data-fit.com" className="df-footer-link"
                target="_blank" rel="noopener noreferrer">www.data-fit.com</a>
              <span className="df-footer-sep">·</span>
              <a href="https://decisionflow.data-fit.com/privacy/" className="df-footer-link" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              <a href="https://decisionflow.data-fit.com/terms/" className="df-footer-link" target="_blank" rel="noopener noreferrer">Terms of Service</a>
              <a href="https://decisionflow.data-fit.com/support/" className="df-footer-link" target="_blank" rel="noopener noreferrer">Support</a>
            </div>
          </div>

        </div>
      </div>

      {/* ══ Cookie notice bar ══ */}
      {!cookieDismissed && (
        <div className="df-cookie-bar" role="region" aria-label="Cookie notice">
          <div className="df-cookie-text">
            DecisionFlow uses strictly necessary cookies to keep you signed in securely.
            No tracking or advertising cookies are used.{" "}
            <a href="#" className="df-cookie-link">Cookie Policy</a>
          </div>
          <div>
            <button className="df-cookie-dismiss" onClick={dismissCookie}>Got it</button>
          </div>
        </div>
      )}
    </>
  );
}
